import {Injectable} from '@angular/core';
import {CytoscapeService} from './cy-services/cytoscape.service';
import {MapStateService} from '../api/services/map-state.service';
import {OverlayElementManagerService} from './overlay-element-manager.service';
import {drawGradientNode} from '../../cytoscape-extensions/draw-gradient-node';

@Injectable({
  providedIn: 'root'
})
export class MapScreenshotService {
  private cachedFontCss: string | null = null;

  constructor(
    private cyService: CytoscapeService,
    private mapStateService: MapStateService,
    private overlayManager: OverlayElementManagerService
  ) {}

  /**
   * Capture the current map viewport as a PNG blob
   * @param scale Resolution scale factor (2 for thumbnails, 4 for full export)
   * @returns PNG blob or null if capture fails
   */
  async captureMap(scale: number = 2): Promise<Blob | null> {
    const cy = this.cyService.cy;
    if (!cy) return null;

    const container = cy.container() as HTMLElement;
    if (!container) return null;

    const w = container.clientWidth;
    const h = container.clientHeight;

    try {
      // Wait for fonts to load
      if ((document as any).fonts?.ready) {
        try {
          await (document as any).fonts.ready;
        } catch {}
      }

      // Get background color from overlay layer
      const bgEl = document.getElementsByClassName('overlay-layer');
      const bg =
        bgEl.length > 0
          ? (bgEl[0] as HTMLElement).style.backgroundColor || 'white'
          : 'white';

      // 1. Base Cytoscape bitmap of the current viewport
      // @ts-ignore
      const baseBlob: Blob | null = await cy.png({
        full: false,
        output: 'blob-promise',
        scale,
        bg,
      });
      if (!baseBlob) return null;

      // 2. Capture cyCanvas layer with gradient effects for collapsed nodes
      const canvasBlob = await this.captureCanvasLayer(cy, w, h, scale);

      // 3. SVG snapshot of the HTML/SVG label overlay WITH embedded fonts
      const overlayUrl = await this.edgeLabelsToDataUrl(container, w, h);

      // 4. Composite all layers
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.floor(w * scale));
      canvas.height = Math.max(1, Math.floor(h * scale));
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;

      // Draw base layer
      const baseUrl = URL.createObjectURL(baseBlob);
      const baseImg = await this.loadImage(baseUrl);
      ctx.drawImage(baseImg, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(baseUrl);

      // Draw canvas layer (gradients)
      if (canvasBlob) {
        const canvasUrl = URL.createObjectURL(canvasBlob);
        const canvasImg = await this.loadImage(canvasUrl);
        ctx.drawImage(canvasImg, 0, 0, canvas.width, canvas.height);
        URL.revokeObjectURL(canvasUrl);
      }

      // Draw edge labels overlay
      if (overlayUrl) {
        const overlayImg = await this.loadImage(overlayUrl, true);
        ctx.drawImage(overlayImg, 0, 0, canvas.width, canvas.height);
        URL.revokeObjectURL(overlayUrl);
      }

      // Draw Konva overlay elements layer
      const konvaCanvas = this.overlayManager.getStageCanvas();
      if (konvaCanvas) {
        ctx.drawImage(konvaCanvas, 0, 0, canvas.width, canvas.height);
      }

      // Convert to blob
      return new Promise<Blob | null>((resolve) => {
        canvas.toBlob((blob) => {
          resolve(blob);
        }, 'image/png');
      });
    } catch (err) {
      console.error('Error capturing map screenshot:', err);
      return null;
    }
  }

  private async edgeLabelsToDataUrl(
    container: HTMLElement,
    w: number,
    h: number,
  ): Promise<string | null> {
    const overlay = container.querySelector(
      '.html-edge-labels-container',
    ) as HTMLElement | null;
    if (!overlay) return null;

    const ns = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('xmlns', ns);
    svg.setAttribute('width', String(w));
    svg.setAttribute('height', String(h));
    svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
    svg.setAttribute('style', 'background: transparent;');

    // Embed Google Fonts (and any @font-face we can fetch) directly into the SVG
    const style = document.createElementNS(ns, 'style');
    style.setAttribute('type', 'text/css');
    style.textContent = await this.buildEmbeddedFontCss();
    svg.appendChild(style);

    // Curved labels (child <svg> with <text> / <textPath>)
    overlay.querySelectorAll('svg').forEach((childSvg) => {
      const clone = childSvg.cloneNode(true) as SVGSVGElement;

      // Inline computed styles for all relevant text nodes
      const origTexts = childSvg.querySelectorAll('text, tspan, textPath');
      const clonedTexts = clone.querySelectorAll('text, tspan, textPath');
      for (let i = 0; i < origTexts.length && i < clonedTexts.length; i++) {
        const o = origTexts[i] as SVGGraphicsElement;
        const c = clonedTexts[i] as SVGGraphicsElement;
        const cs = getComputedStyle(o as any);
        c.setAttribute('font-family', cs.fontFamily || 'sans-serif');
        c.setAttribute('font-size', cs.fontSize || '12px');
        if (cs.fontWeight) c.setAttribute('font-weight', cs.fontWeight);
        if (cs.fontStyle) c.setAttribute('font-style', cs.fontStyle);
        if (cs.letterSpacing && cs.letterSpacing !== 'normal')
          c.setAttribute('letter-spacing', cs.letterSpacing);
        this.applyResolvedPaint(c, cs);
        if (cs.textAnchor) c.setAttribute('text-anchor', cs.textAnchor as any);
        if (
          (cs as any)['font-variation-settings'] &&
          (cs as any)['font-variation-settings'] !== 'normal'
        ) {
          c.setAttribute(
            'font-variation-settings',
            (cs as any)['font-variation-settings'],
          );
        }
      }

      // Drop width/height from nested svgs so they render in root coords
      clone.removeAttribute('width');
      clone.removeAttribute('height');
      clone.setAttribute('x', '0');
      clone.setAttribute('y', '0');
      clone.setAttribute('overflow', 'visible');

      svg.appendChild(clone);
    });

    // Straight labels (divs absolutely positioned and rotated)
    overlay.querySelectorAll('div').forEach((div) => {
      const el = div as HTMLElement;
      const cs = getComputedStyle(el);
      const left = parseFloat(el.style.left || '0');
      const top = parseFloat(el.style.top || '0');
      const tr = el.style.transform || '';
      const m = /rotate\(([-0-9.]+)rad\)/.exec(tr);
      const angleDeg = m ? (parseFloat(m[1]) * 180) / Math.PI : 0;

      const text = document.createElementNS(ns, 'text');
      text.setAttribute('x', String(left));
      text.setAttribute('y', String(top));
      text.setAttribute('text-anchor', 'middle');
      text.setAttribute('dominant-baseline', 'middle');
      if (angleDeg)
        text.setAttribute('transform', `rotate(${angleDeg} ${left} ${top})`);

      // Inline computed styles
      text.setAttribute('font-family', cs.fontFamily || 'sans-serif');
      text.setAttribute('font-size', cs.fontSize || '12px');
      if (cs.fontWeight) text.setAttribute('font-weight', cs.fontWeight);
      if (cs.fontStyle) text.setAttribute('font-style', cs.fontStyle);
      if (cs.letterSpacing && cs.letterSpacing !== 'normal')
        text.setAttribute('letter-spacing', cs.letterSpacing);
      if (cs.color) text.style.setProperty('fill', cs.color);

      // `-webkit-text-stroke` has no SVG equivalent - re-express the halo as a
      // painted-under stroke so straight labels keep the same readability
      // outline they have on screen.
      const haloColor =
        cs.webkitTextStrokeColor || cs.getPropertyValue('-webkit-text-stroke-color');
      const haloWidth = parseFloat(
        cs.webkitTextStrokeWidth ||
          cs.getPropertyValue('-webkit-text-stroke-width') ||
          '0',
      );
      if (haloColor && haloWidth > 0) {
        text.style.setProperty('stroke', haloColor);
        text.style.setProperty('stroke-width', `${haloWidth}px`);
        text.style.setProperty('stroke-linejoin', 'round');
        text.style.setProperty('stroke-linecap', 'round');
        text.style.setProperty('paint-order', 'stroke fill');
      }

      const fv = (cs as any)['font-variation-settings'];
      if (fv && fv !== 'normal')
        text.setAttribute('font-variation-settings', fv);

      text.textContent = el.textContent || '';
      svg.appendChild(text);
    });

    const serialized = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([serialized], {
      type: 'image/svg+xml;charset=utf-8',
    });
    return URL.createObjectURL(blob);
  }

  /**
   * Copy the *resolved* paint of a live label element onto its export clone.
   *
   * The live edge labels are styled with CSS custom properties
   * (`--html-edge-label-color`, `--smap-background-color`) that are set on
   * `document.documentElement` from the smap's appearance. The clone is
   * serialized into a standalone SVG document where those custom properties do
   * not exist, so every `var()` collapses: fills declared without a fallback
   * become invalid-at-computed-value-time and inherit, while the stroke falls
   * back to its hardcoded white. Presentation attributes cannot repair this -
   * the clone still carries the original inline `style`, which outranks them.
   * Writing the resolved colors back into the clone's inline style is what
   * actually wins the cascade.
   */
  private applyResolvedPaint(el: SVGElement, cs: CSSStyleDeclaration): void {
    const fill = cs.fill || cs.getPropertyValue('fill');
    if (fill && fill !== 'none') el.style.setProperty('fill', fill);

    const stroke = cs.stroke || cs.getPropertyValue('stroke');
    const strokeWidth = parseFloat(
      cs.strokeWidth || cs.getPropertyValue('stroke-width') || '',
    );
    if (stroke && stroke !== 'none') {
      el.style.setProperty('stroke', stroke);
      if (Number.isFinite(strokeWidth) && strokeWidth > 0) {
        el.style.setProperty('stroke-width', `${strokeWidth}px`);
      }
      el.style.setProperty('stroke-linejoin', 'round');
      el.style.setProperty('stroke-linecap', 'round');
      el.style.setProperty('paint-order', 'stroke fill');
    } else {
      el.style.setProperty('stroke', 'none');
    }
  }

  private async buildEmbeddedFontCss(): Promise<string> {
    // Use cached font CSS if available (expensive to fetch)
    if (this.cachedFontCss) {
      return this.cachedFontCss;
    }

    let css = '';

    // Inline any Google Fonts stylesheets
    const links = Array.from(
      document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]'),
    ).filter((l) => /fonts\.googleapis\.com/i.test(l.href));

    for (const link of links) {
      try {
        const cssText = await (await fetch(link.href, { mode: 'cors' })).text();

        // For each url(...) in the stylesheet, fetch the font binary and convert to data: URL
        const urlRegex = /url\((['"]?)([^'")]+)\1\)/g;
        let rewritten = cssText;
        const urls: string[] = [];
        let m: RegExpExecArray | null;
        while ((m = urlRegex.exec(cssText)) !== null) {
          urls.push(m[2]);
        }

        for (const u of urls) {
          try {
            const abs = new URL(u, link.href).toString();
            const res = await fetch(abs, { mode: 'cors' });
            const buf = await res.arrayBuffer();
            const b64 = this.base64FromArrayBuffer(buf);
            const ct =
              res.headers.get('content-type') || this.guessFontMime(abs);
            const dataUrl = `data:${ct};base64,${b64}`;
            rewritten = rewritten.replace(
              new RegExp(this.escapeRegex(u), 'g'),
              dataUrl,
            );
          } catch {}
        }

        css += `\n/* embedded google font */\n${rewritten}\n`;
      } catch {}
    }

    // Embed maporium icon font for resource and comment icons
    try {
      const maporiumWoff2Url =
        '/assets/font-maporium/font/maporium.woff2?79352924';
      const res = await fetch(maporiumWoff2Url);
      const buf = await res.arrayBuffer();
      const b64 = this.base64FromArrayBuffer(buf);
      const dataUrl = `data:font/woff2;base64,${b64}`;

      css += `\n/* embedded maporium icon font */\n`;
      css += `@font-face {\n`;
      css += `  font-family: 'maporium';\n`;
      css += `  src: url('${dataUrl}') format('woff2');\n`;
      css += `  font-weight: normal;\n`;
      css += `  font-style: normal;\n`;
      css += `}\n`;
    } catch (e) {
      console.warn('Could not embed maporium icon font:', e);
    }

    // Cache the result
    this.cachedFontCss = css;
    return css;
  }

  private base64FromArrayBuffer(buf: ArrayBuffer): string {
    let binary = '';
    const bytes = new Uint8Array(buf);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) binary += String.fromCharCode(bytes[i]);
    return btoa(binary);
  }

  private guessFontMime(url: string): string {
    if (/\.woff2(\?|$)/i.test(url)) return 'font/woff2';
    if (/\.woff(\?|$)/i.test(url)) return 'font/woff';
    if (/\.ttf(\?|$)/i.test(url)) return 'font/ttf';
    if (/\.otf(\?|$)/i.test(url)) return 'font/otf';
    return 'application/octet-stream';
  }

  private escapeRegex(s: string): string {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  private async captureCanvasLayer(
    cy: any,
    w: number,
    h: number,
    scale: number,
  ): Promise<Blob | null> {
    try {
      // Create a new canvas at the desired scale for manual rendering
      const exportCanvas = document.createElement('canvas');
      exportCanvas.width = Math.max(1, Math.floor(w * scale));
      exportCanvas.height = Math.max(1, Math.floor(h * scale));
      const exportCtx = exportCanvas.getContext('2d');
      if (!exportCtx) return null;

      // Keep canvas transparent
      exportCtx.clearRect(0, 0, exportCanvas.width, exportCanvas.height);

      // Create a temporary cyCanvas-like object for the export
      const tempLayer = {
        getCanvas: () => exportCanvas,
        resetTransform: (ctx: any) => ctx.setTransform(1, 0, 0, 1, 0, 0),
        clear: (ctx: any) =>
          ctx.clearRect(0, 0, exportCanvas.width, exportCanvas.height),
        setTransform: (ctx: any) => {
          const zoom = cy.zoom();
          const pan = cy.pan();
          ctx.scale(scale, scale);
          ctx.scale(zoom, zoom);
          ctx.translate(pan.x, pan.y);
        },
      };

      // Temporarily replace cyCanvas to use our export canvas
      const originalCyCanvas = cy.cyCanvas;
      cy.cyCanvas = () => tempLayer;

      // Manually trigger gradient drawing for collapsed nodes
      drawGradientNode(cy, this.mapStateService);

      // Restore original cyCanvas
      cy.cyCanvas = originalCyCanvas;

      return new Promise<Blob | null>((resolve) => {
        exportCanvas.toBlob((blob) => {
          resolve(blob);
        });
      });
    } catch (e) {
      console.warn('Could not capture canvas layer:', e);
      return null;
    }
  }

  private loadImage(
    url: string,
    crossOrigin = false,
  ): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      if (crossOrigin) img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = url;
    });
  }
}
